import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PersonAdd,
  Delete,
  Edit,
  ContentCopy,
  Business,
  Group,
  Mail,
  Shield,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '../providers/OrganizationProvider';
import { usePermissions } from '../hooks/usePermissions';
import { fetchWithAuth } from '../lib/api';
import { PermissionGate } from '../components/PermissionGate';
import { PERMISSIONS } from '../utils/permissions';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/el';

dayjs.extend(relativeTime);
dayjs.locale('el');

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 0 }}>
      {value === index && children}
    </div>
  );
}

export default function OrganizationSettingsPage() {
  const { organization, organizationId } = useOrganization();
  const { can, isOwner, isAdmin, assignableRoles, getRoleName, getRoleColor } = usePermissions();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'employee' });
  const [editRoleDialog, setEditRoleDialog] = useState({ open: false, member: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', data: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [inviteLink, setInviteLink] = useState(null);

  const { data: membersData, isLoading } = useQuery({
    queryKey: ['organization-members', organizationId],
    queryFn: async () => {
      const res = await fetchWithAuth(`/api/organizations/${organizationId}/members`);
      if (!res.ok) throw new Error('Failed to fetch members');
      return res.json();
    },
    enabled: !!organizationId && can(PERMISSIONS.ORG_MEMBERS_VIEW),
  });

  const members = membersData?.members || [];
  const pendingInvitations = membersData?.pendingInvitations || [];

  const inviteMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetchWithAuth(`/api/organizations/${organizationId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to send invitation');
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['organization-members']);
      setInviteDialog(false);
      setInviteForm({ email: '', role: 'employee' });
      setInviteLink(data.inviteLink);
      if (data.emailSent) {
        showSnackbar('Η πρόσκληση στάλθηκε με email επιτυχώς!', 'success');
      } else {
        showSnackbar('Η πρόσκληση δημιουργήθηκε. Αντιγράψτε τον σύνδεσμο για να τον μοιραστείτε.', 'info');
      }
    },
    onError: (error) => showSnackbar(error.message, 'error'),
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }) => {
      const res = await fetchWithAuth(`/api/organizations/${organizationId}/members/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update role');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organization-members']);
      setEditRoleDialog({ open: false, member: null });
      showSnackbar('Ο ρόλος ενημερώθηκε επιτυχώς!', 'success');
    },
    onError: (error) => showSnackbar(error.message, 'error'),
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId) => {
      const res = await fetchWithAuth(`/api/organizations/${organizationId}/members/${userId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to remove member');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organization-members']);
      setConfirmDialog({ open: false, type: '', data: null });
      showSnackbar('Το μέλος αφαιρέθηκε επιτυχώς!', 'success');
    },
    onError: (error) => showSnackbar(error.message, 'error'),
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId) => {
      const res = await fetchWithAuth(`/api/organizations/${organizationId}/invitations/${invitationId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to cancel invitation');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organization-members']);
      setConfirmDialog({ open: false, type: '', data: null });
      showSnackbar('Η πρόσκληση ακυρώθηκε!', 'success');
    },
    onError: (error) => showSnackbar(error.message, 'error'),
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleInvite = () => {
    if (!inviteForm.email) {
      showSnackbar('Εισάγετε email', 'error');
      return;
    }
    inviteMutation.mutate(inviteForm);
  };

  const handleUpdateRole = () => {
    if (!editRoleDialog.member) return;
    updateRoleMutation.mutate({
      userId: editRoleDialog.member.clerk_user_id,
      role: editRoleDialog.newRole,
    });
  };

  const handleRemoveMember = () => {
    if (confirmDialog.type === 'remove' && confirmDialog.data) {
      removeMemberMutation.mutate(confirmDialog.data.clerk_user_id);
    }
  };

  const handleCancelInvitation = () => {
    if (confirmDialog.type === 'cancel-invite' && confirmDialog.data) {
      cancelInvitationMutation.mutate(confirmDialog.data.id);
    }
  };

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      showSnackbar('Ο σύνδεσμος αντιγράφηκε!', 'success');
    }
  };

  const getInitials = (member) => {
    if (member.display_name && member.display_name !== member.clerk_user_id) {
      return member.display_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    }
    if (member.email) return member.email.substring(0, 2).toUpperCase();
    return member.clerk_user_id.substring(0, 2).toUpperCase();
  };

  if (!can(PERMISSIONS.ORG_MEMBERS_VIEW)) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Δεν έχετε πρόσβαση στις ρυθμίσεις του οργανισμού.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            background: 'linear-gradient(135deg, #008060 0%, #004c3f 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0, 128, 96, 0.3)',
          }}>
            <Group sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Οργανισμός & Μέλη
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {organization?.name}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{
        mb: { xs: 2, md: 3 }, borderRadius: 3, overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{
            bgcolor: 'rgba(0,0,0,0.01)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #008060, #004c3f)',
            },
            '& .MuiTab-root': {
              minHeight: { xs: 56, md: 64 },
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontWeight: 600,
              textTransform: 'none',
              '&.Mui-selected': {
                color: '#008060',
                bgcolor: 'rgba(0,128,96,0.04)',
              },
            },
          }}
        >
          <Tab icon={<Group />} label="Μέλη" iconPosition="start" />
          <Tab icon={<Business />} label="Γενικά" iconPosition="start" />
        </Tabs>

        {/* Members Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Μέλη Οργανισμού
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {members.length} {members.length === 1 ? 'μέλος' : 'μέλη'}
                </Typography>
              </Box>
              <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_INVITE}>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setInviteDialog(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: '0 4px 14px rgba(0,128,96,0.3)',
                  }}
                >
                  Πρόσκληση
                </Button>
              </PermissionGate>
            </Box>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#008060' }} />
              </Box>
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {members.map((member) => (
                  <Card key={member.clerk_user_id} variant="outlined" sx={{
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.08)',
                    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                    transition: 'box-shadow 0.2s',
                  }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Avatar sx={{
                          bgcolor: getRoleColor(member.role),
                          width: 40, height: 40,
                          fontSize: '0.85rem', fontWeight: 700,
                        }}>
                          {getInitials(member)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                            {member.display_name || member.clerk_user_id}
                          </Typography>
                          {member.email && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {member.email}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={getRoleName(member.role)}
                          size="small"
                          sx={{
                            backgroundColor: getRoleColor(member.role),
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(member.joined_at).fromNow()}
                        </Typography>
                        {member.role !== 'owner' && (
                          <Box>
                            <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_EDIT}>
                              <IconButton size="small" onClick={() => setEditRoleDialog({ open: true, member, newRole: member.role })}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </PermissionGate>
                            <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_REMOVE}>
                              <IconButton size="small" color="error" onClick={() => setConfirmDialog({ open: true, type: 'remove', data: member })}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </PermissionGate>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Χρήστης</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ρόλος</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Προστέθηκε</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ενέργειες</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.clerk_user_id} sx={{ '&:hover': { bgcolor: 'rgba(0,128,96,0.02)' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{
                              bgcolor: getRoleColor(member.role),
                              width: 40, height: 40,
                              fontSize: '0.85rem', fontWeight: 700,
                            }}>
                              {getInitials(member)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>
                                {member.display_name || member.clerk_user_id}
                              </Typography>
                              {member.email && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Mail sx={{ fontSize: 12 }} />
                                  {member.email}
                                </Typography>
                              )}
                              {member.is_default === 1 && (
                                <Chip label="Προεπιλογή" size="small" sx={{ height: 18, fontSize: 10, mt: 0.5 }} />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<Shield sx={{ fontSize: '14px !important', color: 'white !important' }} />}
                            label={getRoleName(member.role)}
                            size="small"
                            sx={{
                              backgroundColor: getRoleColor(member.role),
                              color: 'white',
                              fontWeight: 600,
                              '& .MuiChip-icon': { ml: 0.5 },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(member.joined_at).fromNow()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {member.role !== 'owner' && (
                            <>
                              <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_EDIT}>
                                <Tooltip title="Αλλαγή ρόλου">
                                  <IconButton
                                    size="small"
                                    onClick={() => setEditRoleDialog({ open: true, member, newRole: member.role })}
                                    sx={{ '&:hover': { color: '#008060' } }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </PermissionGate>
                              <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_REMOVE}>
                                <Tooltip title="Αφαίρεση">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setConfirmDialog({ open: true, type: 'remove', data: member })}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </PermissionGate>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pending Invitations */}
            {pendingInvitations.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Mail sx={{ color: '#008060', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Εκκρεμείς Προσκλήσεις
                  </Typography>
                  <Chip label={pendingInvitations.length} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                </Box>
                <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem' }}>Ρόλος</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem' }}>Λήγει</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#555', fontSize: '0.8rem' }}>Ενέργειες</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingInvitations.map((inv) => (
                        <TableRow key={inv.id} sx={{ '&:hover': { bgcolor: 'rgba(0,128,96,0.02)' } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{inv.email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getRoleName(inv.role)}
                              size="small"
                              sx={{ backgroundColor: getRoleColor(inv.role), color: 'white', fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {dayjs(inv.expires_at).fromNow()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Ακύρωση">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setConfirmDialog({ open: true, type: 'cancel-invite', data: inv })}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* General Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Στοιχεία Οργανισμού
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2.5,
            }}>
              {[
                { label: 'Όνομα', value: organization?.name },
                { label: 'ΑΦΜ', value: organization?.vat_number || '-' },
                {
                  label: 'Πλάνο',
                  component: (
                    <Chip
                      label={
                        organization?.plan_status === 'trialing'
                          ? `${organization?.plan === 'business' ? 'Business' : 'Pro'} Trial`
                          : organization?.plan === 'business'
                            ? 'Business'
                            : organization?.plan === 'pro'
                              ? 'Pro'
                              : 'Free'
                      }
                      size="small"
                      sx={{
                        fontWeight: 600,
                        ...(organization?.plan === 'pro' || organization?.plan === 'business' ? {
                          bgcolor: 'rgba(0,128,96,0.1)', color: '#008060',
                        } : {
                          bgcolor: 'rgba(0,0,0,0.06)', color: '#666',
                        }),
                      }}
                    />
                  ),
                },
                {
                  label: 'Ο ρόλος σας',
                  component: (
                    <Chip
                      label={getRoleName(organization?.role)}
                      size="small"
                      sx={{ backgroundColor: getRoleColor(organization?.role), color: 'white', fontWeight: 600 }}
                    />
                  ),
                },
              ].map((item, idx) => (
                <Box key={idx} sx={{
                  p: 2.5, bgcolor: 'rgba(0,0,0,0.015)', borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {item.label}
                  </Typography>
                  {item.component || (
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {item.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </TabPanel>
      </Paper>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Πρόσκληση Νέου Μέλους</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              placeholder="user@example.com"
            />
            <FormControl fullWidth>
              <InputLabel>Ρόλος</InputLabel>
              <Select
                value={inviteForm.role}
                label="Ρόλος"
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              >
                {assignableRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {getRoleName(role)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteDialog(false)} sx={{ borderRadius: 2 }}>Ακύρωση</Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={inviteMutation.isPending}
            sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}
          >
            {inviteMutation.isPending ? <CircularProgress size={20} /> : 'Αποστολή'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Link Dialog */}
      <Dialog open={!!inviteLink} onClose={() => setInviteLink(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Σύνδεσμος Πρόσκλησης</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            Η πρόσκληση δημιουργήθηκε! Στείλτε τον παρακάτω σύνδεσμο στον χρήστη.
          </Alert>
          <TextField
            fullWidth
            value={inviteLink || ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={copyInviteLink}>
                  <ContentCopy />
                </IconButton>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteLink(null)} sx={{ borderRadius: 2 }}>Κλείσιμο</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialog.open} onClose={() => setEditRoleDialog({ open: false, member: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Αλλαγή Ρόλου</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, minWidth: 300 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Χρήστης: {editRoleDialog.member?.display_name || editRoleDialog.member?.clerk_user_id}
            </Typography>
            {editRoleDialog.member?.email && (
              <Typography variant="caption" color="text.secondary">
                {editRoleDialog.member.email}
              </Typography>
            )}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Νέος Ρόλος</InputLabel>
              <Select
                value={editRoleDialog.newRole || ''}
                label="Νέος Ρόλος"
                onChange={(e) => setEditRoleDialog({ ...editRoleDialog, newRole: e.target.value })}
              >
                {assignableRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {getRoleName(role)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditRoleDialog({ open: false, member: null })} sx={{ borderRadius: 2 }}>Ακύρωση</Button>
          <Button
            variant="contained"
            onClick={handleUpdateRole}
            disabled={updateRoleMutation.isPending}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {updateRoleMutation.isPending ? <CircularProgress size={20} /> : 'Αποθήκευση'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: '', data: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmDialog.type === 'remove' ? 'Αφαίρεση Μέλους' : 'Ακύρωση Πρόσκλησης'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.type === 'remove'
              ? `Είστε σίγουροι ότι θέλετε να αφαιρέσετε αυτό το μέλος;`
              : `Είστε σίγουροι ότι θέλετε να ακυρώσετε την πρόσκληση για ${confirmDialog.data?.email};`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', data: null })} sx={{ borderRadius: 2 }}>
            Όχι
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDialog.type === 'remove' ? handleRemoveMember : handleCancelInvitation}
            disabled={removeMemberMutation.isPending || cancelInvitationMutation.isPending}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Ναι
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
